/**
 * i18next wiring (doc 00 §10, plan 14 §3.3). Framework-agnostic: builds the
 * shared init config and a standalone i18next instance. React/RN bindings
 * (react-i18next, expo-localization) are applied in each app's own run so this
 * package stays dependency-light and Deno-vendorable.
 *
 * Fallback chain: sq → mk, en → mk. A key missing from sq/en renders the mk
 * value (never a raw key), so partial Albanian translation is safe (PL-01).
 */

import i18next, { type i18n, type InitOptions } from 'i18next';

import en from './catalogs/en.json' with { type: 'json' };
import mk from './catalogs/mk.json' with { type: 'json' };
import sq from './catalogs/sq.json' with { type: 'json' };
import type { SupportedLocale } from './format.js';

const isDev = typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'development';

/** Shared i18next init options for a given starting locale. */
export function buildI18nConfig(locale: SupportedLocale): InitOptions {
  return {
    lng: locale,
    fallbackLng: { sq: ['mk'], en: ['mk'], default: ['mk'] },
    resources: {
      mk: { translation: mk },
      sq: { translation: sq },
      en: { translation: en },
    },
    interpolation: { escapeValue: false },
    returnNull: false,
    saveMissing: isDev,
  };
}

/**
 * Create and initialize a standalone i18next instance for the given locale.
 * Each app creates its own instance (admin-web binds it via react-i18next,
 * member-app/terminal likewise) so there is no shared mutable global.
 */
export async function createI18n(locale: SupportedLocale): Promise<i18n> {
  const instance = i18next.createInstance();
  await instance.init(buildI18nConfig(locale));
  return instance;
}

export { i18next };
